import { ReactNode } from 'react';
import styles from './PageHeader.module.css';

export interface Breadcrumb {
  label: string;
  /** אם קיים — קישור; אחרת טקסט בלבד (לרוב פריט אחרון) */
  href?: string;
}

export interface PageHeaderProps {
  /** כותרת ראשית (h1) */
  title: string;
  /** קישוט קטן מעל הכותרת ("◆ דשבורד") */
  eyebrow?: string;
  /** תיאור משני מתחת לכותרת */
  subtitle?: string;
  /** מערך ניווט: { label, href? } */
  breadcrumbs?: Breadcrumb[];
  /** אזור כפתורים (בצד הלוגי end) */
  actions?: ReactNode;
  /** מקום ל-MonthYearSelector / filters (בין title ל-actions) */
  filterSlot?: ReactNode;
  className?: string;
}

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

/**
 * Header אחיד למסכי Profitability.
 *
 * 6 slots: `eyebrow`, `breadcrumbs`, `title`, `subtitle`, `actions`, `filterSlot`.
 * משתמש ב-semantic `<header>` עם `role="banner"` (אוטומטי).
 *
 * @example
 * <PageHeader title="רווחיות — דשבורד" />
 *
 * @example
 * <PageHeader
 *   eyebrow="◆ דשבורד"
 *   title="רווחיות מאי 2026"
 *   subtitle="מבוסס נתונים מעודכנים"
 *   breadcrumbs={[
 *     { label: 'ראשי', href: '/' },
 *     { label: 'רווחיות', href: '/profitability' },
 *     { label: 'דשבורד' },
 *   ]}
 *   filterSlot={<MonthYearSelector ... />}
 *   actions={<><button>ייצוא</button></>}
 * />
 */
export const PageHeader = ({
  title,
  eyebrow,
  subtitle,
  breadcrumbs,
  actions,
  filterSlot,
  className,
}: PageHeaderProps): JSX.Element => (
  <header className={cx(styles.root, className)} data-testid="page-header">
    {breadcrumbs && breadcrumbs.length > 0 && (
      <nav
        className={styles.breadcrumbs}
        aria-label="ניווט מקושר"
        data-testid="page-header-breadcrumbs"
      >
        <ol className={styles.breadcrumbList}>
          {breadcrumbs.map((bc, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <li key={idx} className={styles.breadcrumbItem}>
                {bc.href && !isLast ? (
                  <a href={bc.href} className={styles.breadcrumbLink}>
                    {bc.label}
                  </a>
                ) : (
                  <span
                    className={styles.breadcrumbCurrent}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {bc.label}
                  </span>
                )}
                {!isLast && (
                  <span className={styles.breadcrumbSep} aria-hidden="true">
                    /
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    )}

    <div className={styles.titleRow}>
      <div className={styles.titleBlock}>
        {eyebrow && (
          <div className={styles.eyebrow} data-testid="page-header-eyebrow">
            {eyebrow}
          </div>
        )}
        <h1 className={styles.title}>{title}</h1>
        {subtitle && (
          <p className={styles.subtitle} data-testid="page-header-subtitle">
            {subtitle}
          </p>
        )}
      </div>

      {(filterSlot || actions) && (
        <div className={styles.controls}>
          {filterSlot && (
            <div className={styles.filterSlot} data-testid="page-header-filter">
              {filterSlot}
            </div>
          )}
          {actions && (
            <div className={styles.actions} data-testid="page-header-actions">
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  </header>
);
